'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useGetInvestmentAccount from './useGetInvestmentAccount';
import { setUserData, updateUserField } from 'redux/reducers/user/slice';
import { getUserRequest } from 'services/user';
import { identify } from 'utils/analytics';
import { STORAGE_KEYS } from 'utils/constants/localStorage';

const isAuthenticationValidationError = (error) => {
  const status = error?.response?.status ?? error?.status;
  return status === 401 || status === 403;
};

const useGetUser = () => {
  const dispatch = useDispatch();
  const [isAccredited, setIsAccredited] = useState(false);

  const {
    data: user,
    error,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUserRequest(),
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { data: investmentAccounts } = useGetInvestmentAccount({
    primaryUserId: user?.primaryUserId,
  });

  useEffect(() => {
    if (isError && isAuthenticationValidationError(error)) {
      dispatch(
        updateUserField({
          key: 'hasInvestmentAccount',
          value: false,
        }),
      );
      dispatch(
        updateUserField({
          key: 'signedIn',
          value: false,
        }),
      );
      dispatch(
        updateUserField({
          key: 'userAccounts',
          value: [],
        }),
      );
      dispatch(
        updateUserField({
          key: 'investmentAccountStatus',
          value: {
            investmentAccount: null,
            hasInvestmentAccount: false,
            investmentAccountPending: false,
            investmentAccountActionRequired: false,
            investmentAccountClosed: false,
            investmentAccountFrozen: false,
          },
        }),
      );
    }
  }, [error, isError, dispatch]);

  useEffect(() => {
    if (user?.primaryUserId) {
      identify(user);
      dispatch(
        setUserData({
          value: user,
        }),
      );
      dispatch(
        updateUserField({
          key: 'signedIn',
          value: true,
        }),
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (investmentAccounts && Array.isArray(investmentAccounts)) {
      const checkInvestmentAccount = async () => {
        const investmentAccountStatus = {
          hasInvestmentAccount: false,
          investmentAccountPending: false,
          investmentAccountActionRequired: false,
        };

        await investmentAccounts.reduce(async (item, account) => {
          await item;

          if (['OPEN', 'APPROVED'].indexOf(account.accountStatus) > -1) {
            investmentAccountStatus.hasInvestmentAccount = true;
            investmentAccountStatus.investmentAccount = account;
          }

          if (['PENDING', 'CUSTODY_TRANSFER'].indexOf(account.accountStatus) > -1) {
            investmentAccountStatus.investmentAccountPending = true;
          }

          if (account.custodianAccountStatus === 'ACTION_REQUIRED') {
            investmentAccountStatus.investmentAccountActionRequired = true;
          }

          if (['CLOSED'].indexOf(account.accountStatus) > -1) {
            investmentAccountStatus.investmentAccountClosed = true;
          }

          if (['FROZEN'].indexOf(account.accountStatus) > -1) {
            investmentAccountStatus.investmentAccountFrozen = true;
          }

          if (investmentAccountStatus.investmentAccount) {
            const owningEntities = investmentAccountStatus.investmentAccount.owningEntities;
            const notAccredited =
              !owningEntities?.length ||
              owningEntities.some((entity) => entity?.accreditationStatus === 'NOT_ACCREDITED');
            setIsAccredited(!notAccredited);
          } else {
            setIsAccredited(false);
          }
        }, Promise.resolve());

        if (
          investmentAccountStatus.hasInvestmentAccount ||
          investmentAccountStatus.investmentAccountPending
        ) {
          try {
            localStorage.removeItem(STORAGE_KEYS.INVESTOR_TYPE_V2);
          } catch {}
        }

        dispatch(
          updateUserField({
            key: 'investmentAccountStatus',
            value: investmentAccountStatus,
          }),
        );
        dispatch(
          updateUserField({
            key: 'userAccounts',
            value: investmentAccounts,
          }),
        );
        // Only fire once per session per user, and strip PII from the payload
        if (user?.primaryUserId && !window._investmentAccountStatusFired?.[user?.primaryUserId]) {
          window._investmentAccountStatusFired = window._investmentAccountStatusFired || {};
          window._investmentAccountStatusFired[user.primaryUserId] = true;
          window?.dataLayer?.push({
            event: 'user-investment-account-status',
            user: {
              investmentAccountStatus: {
                hasInvestmentAccount: investmentAccountStatus.hasInvestmentAccount,
                investmentAccountPending: investmentAccountStatus.investmentAccountPending,
                investmentAccountActionRequired:
                  investmentAccountStatus.investmentAccountActionRequired,
                investmentAccountClosed: investmentAccountStatus.investmentAccountClosed,
                investmentAccountFrozen: investmentAccountStatus.investmentAccountFrozen,
              },
            },
          });
        }
      };

      checkInvestmentAccount();
    }
  }, [investmentAccounts, dispatch, user?.primaryUserId]);

  return {
    data: user,
    isAccredited,
    error,
    isLoading,
    isError,
    refetch,
    isFetching,
  };
};

export default useGetUser;
